import type {Token} from '../tokenize/token'
import type {Alignment, CstNode, TableNode} from './syntax-tree'

function determineTableAlignment(table: CstNode): Alignment[] | undefined {
  const delimiterRow = table.children
    .find(n => n.type === 'tableHead')
    ?.children.find(n => n.type === 'tableDelimiterRow')
  return delimiterRow?.children?.map(delimiterCell => {
    const cellContent = delimiterCell.children.find(n => n.type === 'tableContent')
    // If the cell ends with a marker (colon), it could be right or centered
    if (cellContent?.children.at(-1)?.type === 'tableDelimiterMarker')
      // If it also starts with a colon, it's centered
      return cellContent.children[0]?.type === 'tableDelimiterMarker' ? 'center' : 'right'
    // In all other cases, including no colon at all, it's left
    return 'left'
  })
}

/** Add alignment data to all cells in the table. */
function enrichTableCells(table: TableNode, alignment: Alignment[]) {
  // It's tempting to use `walkCst` here to just find every tableData or tableHeader cell in the table's tree, but this
  // would be incorrect because it would also align cells in nested tables
  for (const tableSection of table.children)
    if (tableSection.type === 'tableHead' || tableSection.type === 'tableBody')
      for (const row of tableSection.children)
        if (row.type === 'tableRow')
          for (const [i, cell] of row.children.entries())
            if (cell.type === 'tableData' || cell.type === 'tableHeader') {
              cell.alignment = alignment[i]
            }
}

function enrichNode(node: CstNode, source: string) {
  switch (node.type) {
    case 'link':
      {
        const destinationNode = node.children
          .find(n => n.type === 'resource')
          ?.children.find(n => n.type === 'resourceDestination')
        if (destinationNode) node.href = source.slice(destinationNode.start, destinationNode.end)
      }
      break
    case 'atxHeading':
      {
        const sequence = node.children.find(n => n.type === 'atxHeadingSequence')
        if (sequence) {
          const level = sequence.end - sequence.start
          node.depth = level
        }
      }
      break
    case 'setextHeading':
      {
        const sequence = node.children.find(n => n.type === 'setextHeadingLine')
        if (sequence) {
          const level = source.slice(sequence.start, sequence.start + 1) === '=' ? 1 : 2
          node.depth = level
        }
      }
      break
    case 'table':
      {
        const alignment = determineTableAlignment(node)
        if (alignment) {
          node.columnAlignments = alignment
          enrichTableCells(node, alignment)
        }
      }
      break
  }
}

/** Convert a flat token array to a concrete syntax tree. */
export function buildCst(tokens: Token[], source: string): CstNode {
  const root: CstNode = {type: 'root', start: 0, end: source.length, children: []}

  /* The last item in the stack is always the node we are currently 'inside' to which we push children. */
  const stack: CstNode[] = [root]

  for (const {direction, ...token} of tokens) {
    if (direction === 'enter') {
      const node = {
        start: token.start,
        end: token.end,
        type: token.type,
        children: [],
      } as CstNode

      const last = stack.at(-1)!
      last.children.push(node)

      stack.push(node)
    } else {
      // Enriching depends on the node's child tree, so we can only do it upon exiting the node
      enrichNode(stack.pop()!, source)
    }
  }

  return root
}
